import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { getFileUrl } from '../../utils/fileUtils';
import AdminNavbar from '../../components/admin/AdminNavbar';
import HtmlEditor from '../../components/admin/HtmlEditor';
import './AdminCommon.css';

function youtubeEmbedUrl(url) {
  if (!url || typeof url !== 'string') return null;
  const u = url.trim();
  if (!u) return null;
  const match = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
}

// Clave a slug para URL: pastoral_multitudes → pastoral-multitudes
function claveToSlug(clave) {
  return (clave || '').replace(/_/g, '-');
}

const GRUPOS_ORDEN = [
  'Centro pastoral para la evangelización y la fe',
  'Centro pastoral para los estados de vida',
  'Centro para la comunión y la participación',
  'Centro pastoral para relaciones y evangelización de lo social',
];

const AdminSeccionesPastoral = () => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [titulo, setTitulo] = useState('');
  const [texto, setTexto] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [telefono, setTelefono] = useState('');
  const [direccion, setDireccion] = useState('');
  const [unaSolaFoto, setUnaSolaFoto] = useState(true);
  const [imagenFile, setImagenFile] = useState(null);
  const [nuevaImagenFile, setNuevaImagenFile] = useState(null);

  const { data: secciones = {}, isLoading, error } = useQuery({
    queryKey: ['secciones-pastoral-admin'],
    queryFn: async () => {
      const res = await api.get('/secciones-pastoral/admin/all');
      return res.data;
    },
    retry: 1,
  });

  const updateMutation = useMutation({
    mutationFn: async ({ clave, titulo, texto, video_url, telefono, direccion, una_sola_foto, imagen }) => {
      const formData = new FormData();
      if (titulo !== undefined) formData.append('titulo', titulo);
      if (texto !== undefined) formData.append('texto', texto);
      if (video_url !== undefined) formData.append('video_url', video_url);
      if (telefono !== undefined) formData.append('telefono', telefono);
      if (direccion !== undefined) formData.append('direccion', direccion);
      formData.append('una_sola_foto', una_sola_foto ? '1' : '0');
      if (imagen) formData.append('imagen', imagen);
      const res = await api.put(`/secciones-pastoral/${clave}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['secciones-pastoral-admin']);
      setEditing(null);
      setImagenFile(null);
      alert('Sección actualizada correctamente.');
    },
    onError: (e) => {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  });

  const addImageMutation = useMutation({
    mutationFn: async ({ clave, imagen }) => {
      const formData = new FormData();
      formData.append('imagen', imagen);
      await api.post(`/secciones-pastoral/${clave}/imagenes`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['secciones-pastoral-admin']);
      setNuevaImagenFile(null);
      alert('Imagen añadida.');
    },
    onError: (e) => {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  });

  const deleteImageMutation = useMutation({
    mutationFn: async ({ clave, imagenId }) => {
      await api.delete(`/secciones-pastoral/${clave}/imagenes/${imagenId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['secciones-pastoral-admin']);
    },
    onError: (e) => {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  });

  const openEdit = (clave) => {
    const s = secciones[clave];
    setEditing(clave);
    setTitulo(s?.titulo || '');
    setTexto(s?.texto || '');
    setVideoUrl(s?.video_url || '');
    setTelefono(s?.telefono || '');
    setDireccion(s?.direccion || '');
    setUnaSolaFoto(s?.una_sola_foto === 1 || s?.una_sola_foto === undefined);
    setImagenFile(null);
    setNuevaImagenFile(null);
  };

  const handleSave = (clave) => {
    updateMutation.mutate({
      clave,
      titulo,
      texto,
      video_url: videoUrl.trim() || '',
      telefono,
      direccion,
      una_sola_foto: unaSolaFoto,
      imagen: imagenFile || undefined
    });
  };

  const handleAddImage = (clave) => {
    if (!nuevaImagenFile) { alert('Seleccione una imagen.'); return; }
    addImageMutation.mutate({ clave, imagen: nuevaImagenFile });
  };

  const clavesOrdenadas = Object.keys(secciones).sort(
    (a, b) => (secciones[a]?.orden ?? 0) - (secciones[b]?.orden ?? 0)
  );

  // Agrupar por grupo
  const porGrupo = {};
  clavesOrdenadas.forEach(clave => {
    const s = secciones[clave];
    const g = s?.grupo || 'Otros';
    if (!porGrupo[g]) porGrupo[g] = [];
    porGrupo[g].push(clave);
  });

  const gruposOrdenados = [...new Set(GRUPOS_ORDEN.filter(g => porGrupo[g]?.length))];
  Object.keys(porGrupo).filter(g => !GRUPOS_ORDEN.includes(g)).forEach(g => gruposOrdenados.push(g));

  if (isLoading) return <div className="admin-common"><AdminNavbar /><div className="container">Cargando...</div></div>;

  return (
    <div className="admin-common">
      <AdminNavbar />
      <div className="container">
        <div className="admin-header">
          <Link to="/admin" className="btn btn-secondary">← Panel</Link>
          <h1>Secciones Pastoral</h1>
          <p>Editar título, descripción (HTML), teléfono, dirección, imágenes y video de cada ítem del menú Pastoral.</p>
        </div>

        {error && (
          <div className="admin-alert admin-alert-danger">
            <strong>No se pudieron cargar las secciones.</strong>
            <p style={{ margin: '0.5rem 0 0' }}>
              {error.response?.data?.error || error.message || 'Error de conexión con el servidor.'}
            </p>
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem' }}>
              <strong>Solución:</strong> Ejecute en MySQL el script consolidado:{' '}
              <code>database/setup_secciones_pastoral_completo.sql</code>
              <br />
              <small>Desde consola: <code>mysql -u USUARIO -p NOMBRE_BD &lt; database/setup_secciones_pastoral_completo.sql</code></small>
            </p>
          </div>
        )}

        {!error && clavesOrdenadas.length === 0 && (
          <div className="admin-alert admin-alert-warning">
            No hay secciones. Cree la tabla e inserte datos ejecutando en MySQL: <code>database/secciones_pastoral.sql</code>
          </div>
        )}

        <div className="secciones-pastoral-admin">
          {gruposOrdenados.map(grupo => (
            <div key={grupo} className="seccion-pastoral-grupo">
              <h3 className="seccion-pastoral-grupo-title">{grupo}</h3>
              <div className="seccion-pastoral-grupo-items">
                {porGrupo[grupo]?.map(clave => {
                  const s = secciones[clave];
                  const esMapaDiocesano = clave === 'proceso_diocesano';
                  const embedUrl = youtubeEmbedUrl(s?.video_url);
                  const slug = claveToSlug(clave);
                  return (
                    <div key={clave} className="seccion-pastoral-card">
                      <h2>{s?.titulo || clave}</h2>
                      <p className="seccion-pastoral-slug">/pastoral/{slug}</p>
                      {editing === clave ? (
                        <div className="seccion-edit-form">
                          <label>Título</label>
                          <input type="text" value={titulo} onChange={e => setTitulo(e.target.value)} />

                          <HtmlEditor
                            label="Texto / Descripción (HTML)"
                            value={texto}
                            onChange={setTexto}
                            rows={8}
                            placeholder="Escribe aquí. Usa los botones para negrita, cursiva, listas y enlaces."
                          />

                          <label>URL de video de YouTube</label>
                          <input
                            type="url"
                            value={videoUrl}
                            onChange={e => setVideoUrl(e.target.value)}
                            placeholder="https://www.youtube.com/watch?v=..."
                          />

                          <label>Teléfono</label>
                          <input type="text" value={telefono} onChange={e => setTelefono(e.target.value)} placeholder="Ej: +57 312 123 4567" />

                          <label>Dirección</label>
                          <input type="text" value={direccion} onChange={e => setDireccion(e.target.value)} placeholder="Dirección de contacto" />

                          {!esMapaDiocesano && (
                            <label>
                              <input type="checkbox" checked={unaSolaFoto} onChange={e => setUnaSolaFoto(e.target.checked)} />
                              Una sola foto. Si no, galería (varias fotos).
                            </label>
                          )}

                          {esMapaDiocesano ? (
                            <>
                              <label>Imagen del mapa</label>
                              <input type="file" accept="image/*" onChange={e => setImagenFile(e.target.files?.[0] || null)} />
                            </>
                          ) : unaSolaFoto ? (
                            <>
                              <label>Imagen (reemplaza la actual)</label>
                              <input type="file" accept="image/*" onChange={e => setImagenFile(e.target.files?.[0] || null)} />
                            </>
                          ) : (
                            <>
                              <label>Imágenes actuales</label>
                              <div className="casa-imagenes-list">
                                {(s?.imagenes || []).filter(img => img.id !== 0).map((img) => (
                                  <div key={img.id} className="casa-imagen-item">
                                    <img src={getFileUrl(img.imagen_url)} alt="" />
                                    <button
                                      type="button"
                                      className="btn btn-danger btn-sm"
                                      onClick={() => deleteImageMutation.mutate({ clave, imagenId: img.id })}
                                      disabled={deleteImageMutation.isLoading}
                                    >
                                      Eliminar
                                    </button>
                                  </div>
                                ))}
                              </div>
                              <label>Añadir otra imagen</label>
                              <div className="casa-add-imagen">
                                <input type="file" accept="image/*" onChange={e => setNuevaImagenFile(e.target.files?.[0] || null)} />
                                <button type="button" className="btn btn-primary" onClick={() => handleAddImage(clave)} disabled={!nuevaImagenFile || addImageMutation.isLoading}>
                                  Subir imagen
                                </button>
                              </div>
                            </>
                          )}

                          <div className="form-actions">
                            <button type="button" className="btn btn-primary" onClick={() => handleSave(clave)} disabled={updateMutation.isLoading}>Guardar</button>
                            <button type="button" className="btn btn-secondary" onClick={() => setEditing(null)}>Cancelar</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {(s?.imagenes?.length > 0 || s?.imagen_url) && (
                            <div className="seccion-preview-img">
                              {s.imagenes?.length > 0 ? (
                                s.imagenes.slice(0, 3).map(img => (
                                  <img key={img.id || img.imagen_url} src={getFileUrl(img.imagen_url)} alt="" />
                                ))
                              ) : (
                                <img src={getFileUrl(s.imagen_url)} alt={s.titulo} />
                              )}
                              {s.imagenes?.length > 3 && <span>+{s.imagenes.length - 3} más</span>}
                            </div>
                          )}
                          {embedUrl && (
                            <div className="seccion-preview-video">
                              <iframe title={`Video ${s?.titulo}`} src={embedUrl} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                            </div>
                          )}
                          {(s?.telefono || s?.direccion || s?.texto) && (
                            <div className="seccion-preview-meta">
                              {s.telefono && <p><strong>Teléfono:</strong> {s.telefono}</p>}
                              {s.direccion && <p><strong>Dirección:</strong> {s.direccion}</p>}
                              {s.texto && (
                                <p className="texto-corto">{(s.texto || '').replace(/<[^>]+>/g, ' ').trim().substring(0, 150)}{(s.texto || '').length > 150 ? '...' : ''}</p>
                              )}
                            </div>
                          )}
                          <p className="seccion-pastoral-tipo">{s?.una_sola_foto === 1 ? 'Una sola foto' : 'Galería (varias fotos)'}</p>
                          <div className="seccion-pastoral-actions">
                            <button type="button" className="btn" onClick={() => openEdit(clave)}>Editar</button>
                            <Link to={`/pastoral/${slug}`} className="btn" target="_blank" rel="noopener noreferrer">Ver página</Link>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminSeccionesPastoral;
